import React, { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";

const PHONE = "561-502-6769";
const HERO_IMG =
  "https://images.unsplash.com/photo-1580446653631-10649c43dee0?auto=format&fit=crop&w=2400&q=80";

const LINES = [
  ["Looking", "For", "A", "Specific"],
  ["Car?", "We'll", "Find", "It."],
];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.7, 0, 0.15, 1], delay: 0.15 + i * 0.08 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOp = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  useEffect(() => {
    // preload
    const img = new Image();
    img.src = HERO_IMG;
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      aria-label="Strive Motors · Hero"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* Parallax background image */}
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img
          src={HERO_IMG}
          alt="Red Ferrari at night — Strive Motors concierge vehicle sourcing"
          className="w-full h-full object-cover object-center"
          loading="eager"
        />
        <motion.div
          style={{ opacity: overlayOp }}
          className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/60 to-[#0A0A0A]"
        />
        <div className="absolute inset-0 spotlight opacity-70 pointer-events-none" />
      </motion.div>

      {/* Top small caption */}
      <div className="relative z-10 pt-32 lg:pt-36 px-6 lg:px-10 max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="flex items-center gap-3 text-[11px] font-mono2 uppercase tracking-[0.28em] text-white/60"
        >
          <span className="inline-block w-8 h-px bg-[#E31E24]" />
          Est. 2010 · Concierge Vehicle Sourcing · Boca Raton · Delray Beach, FL
        </motion.div>
      </div>

      {/* Massive headline with masked line-by-line reveal */}
      <div className="relative z-10 px-6 lg:px-10 max-w-[1400px] mx-auto mt-6 lg:mt-10">
        <h1
          data-testid="hero-headline"
          className="font-anton uppercase leading-[0.85] text-[16vw] md:text-[13vw] lg:text-[10.5vw] tracking-tight"
        >
          <span className="sr-only">Looking For A Specific Car? We'll Find It.</span>
          {LINES.map((line, li) => (
            <div key={li} className="flex flex-wrap gap-x-[0.25em]" aria-hidden="true">
              {line.map((word, wi) => {
                const idx = li * 10 + wi;
                const isAccent =
                  (li === 1 && word === "Find") || (li === 1 && word === "It.");
                return (
                  <span key={wi} className="reveal-mask">
                    <motion.span
                      custom={idx}
                      variants={lineVariants}
                      initial="hidden"
                      animate="show"
                      className={`reveal-line ${isAccent ? "text-[#E31E24] italic font-cormorant font-medium" : ""}`}
                      style={isAccent ? { fontStyle: "italic" } : undefined}
                    >
                      {word}
                    </motion.span>
                  </span>
                );
              })}
            </div>
          ))}
        </h1>
      </div>

      {/* Sub content grid */}
      <div className="relative z-10 px-6 lg:px-10 max-w-[1400px] mx-auto mt-10 lg:mt-14 grid grid-cols-12 gap-6 pb-24">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9 }}
          className="col-span-12 lg:col-span-6 lg:col-start-1 text-white/75 text-[15px] lg:text-[17px] leading-relaxed max-w-xl"
        >
          Whether you're searching for a reliable daily driver, luxury vehicle, truck,
          SUV, sports car, or something unique · Strive Motors sources the right vehicle
          at the right price. Fifteen years of automotive experience, working the market
          on your behalf.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9 }}
          className="col-span-12 lg:col-span-5 lg:col-start-8 flex flex-col gap-5"
        >
          <div className="flex items-center gap-3 text-[11px] font-mono2 uppercase tracking-[0.3em] text-[#E31E24]">
            <span className="inline-block w-2 h-2 bg-[#E31E24] rounded-full animate-pulse" />
            Buy · Sell · Trade
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="#find"
              data-testid="hero-cta-find-car"
              className="group inline-flex items-center justify-between gap-4 bg-[#E31E24] hover:bg-white hover:text-[#0A0A0A] text-white px-6 py-4 transition-colors duration-300"
            >
              <span className="font-anton uppercase tracking-[0.12em] text-lg">
                Find My Car
              </span>
              <ArrowUpRight size={22} className="transition-transform duration-300 group-hover:rotate-45" />
            </a>
            <a
              href="#sell"
              data-testid="hero-cta-sell-car"
              className="group inline-flex items-center justify-between gap-4 border border-white/40 hover:border-white hover:bg-white/5 text-white px-6 py-4 transition-colors duration-300"
            >
              <span className="font-anton uppercase tracking-[0.12em] text-lg">
                Sell or Trade
              </span>
              <ArrowUpRight size={22} className="transition-transform duration-300 group-hover:rotate-45" />
            </a>
          </div>
          <a
            href={`tel:${PHONE}`}
            data-testid="hero-phone-link"
            className="inline-flex items-center gap-3 mt-2 font-mono2 text-sm text-white/70 hover:text-white"
          >
            <Phone size={14} />
            <span>Call direct · {PHONE}</span>
          </a>
        </motion.div>
      </div>

      {/* Bottom stats bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute left-0 right-0 bottom-0 z-10 border-t border-white/10 backdrop-blur-md bg-[#0A0A0A]/60"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center justify-between gap-4 text-[11px] font-mono2 uppercase tracking-[0.24em] text-white/75">
          <div>500+ Vehicles Sourced</div>
          <div className="hidden md:block">Recently Found · 2022 Ford Bronco Badlands</div>
          <div className="hidden md:block">15 Years Experience</div>
          <div className="text-white/60">Scroll ↓</div>
        </div>
      </motion.div>
    </section>
  );
}
